<script setup lang="ts" generic="T extends PejerTableNames, L extends PejerTablesList<T>">
import type { OperatorObject, SearchQuery, SearchQueryKeys } from 'pejer/src/types/types'
import type { SearchableColumn } from 'pejer/src/useSearch'
import { Button } from './ui/button'
import { Input } from './ui/input'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from './ui/select'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu'

import { ChevronLeft, ListFilter, Search, Sparkle, Sparkles, X } from 'lucide-vue-next'
import { Groups, JSSchema, JSSchemaObjectTypes } from 'pejer/src/tables'
import { computed, ref, watch } from 'vue'
import DateRangePicker from './DateRangePicker/DateRangePicker.vue'
import HorizontalLine from './HorizontalLine.vue'
import NumberBetweenInput from './NumberBetweenInput.vue'
import { Badge } from './ui/badge'

import { usePejerBase } from '../stores/pejerData'
import { PejerTableNames, PejerTablesList } from '../types/base'
import ColumnSearchBadge from './ColumnSearchBadge.vue'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip'

const props = defineProps<{
  placeholder: string
  activeColumn: SearchQueryKeys<L>
  searchableColumns: SearchableColumn<L>[]
  filterQuery: SearchQuery<L>
  jsSchema: JSSchema<PejerTablesList<T>>
  autocomplete?: string[]
  groups?: Groups
  multipleLines?: boolean
}>()

const emits = defineEmits<{
  (e: 'onSearch', value: OperatorObject, column?: SearchQueryKeys<L>): void
  (e: 'onInputChange', value: string): void
  (e: 'onActiveColumnChange', value: SearchQueryKeys<L>): void
  (e: 'onRemoveColumn', value: SearchQueryKeys<L>): void
}>()

const inputText = ref<string>(props.filterQuery[props.activeColumn as string]?.['=']?.value || '')
const inputNumberLeft = ref<string>(
  props.filterQuery[props.activeColumn]?.['>=']?.value.toString() || ''
)
const inputNumberRight = ref<string>(
  props.filterQuery[props.activeColumn]?.['<=']?.value.toString() || ''
)

const timer = ref<NodeJS.Timeout | null>(null)

const smartSearchEnabledRef = ref(true)
const store = usePejerBase()
const autoCompleteOpen = ref(false)

watch(
  () => props.activeColumn,
  (col) => {
    inputType.value = getInputType(col)

    if (inputType.value === 'between') {
      inputNumberLeft.value = props.filterQuery[col]?.['>=']?.value.toString() || ''
      inputNumberRight.value = props.filterQuery[col]?.['<=']?.value.toString() || ''
    } else inputText.value = props.filterQuery[col as string]?.['=']?.value || ''
    smartSearchEnabledRef.value = props.filterQuery[col]?.['=']?.smartSearchEnabled || false
  }
)

function onColumnSelect(column: string) {
  if (column === props.activeColumn) return

  inputText.value = null
  autoCompleteOpen.value = false
  emitActiveColumn(column as SearchQueryKeys<L>)
}

function debounce(wait: number, fun: () => void) {
  if (timer.value) clearTimeout(timer.value)

  if (wait == 0) fun()
  else timer.value = setTimeout(fun, wait)
}

function onEnter() {
  debounce(0, emitTextSearch)
}

function onTextInput(e: Event) {
  const value = (e.target as HTMLInputElement).value
  inputText.value = value
  emitAutoCompleteTrigger()
  debounce(750, emitTextSearch)
}

function getSchemaObject(column: string) {
  return store.getSchemaObjectFromFlattenedKeys(props.jsSchema, column)
}

function onNumberInput(inputs: [string | null, string | null]) {
  inputNumberLeft.value = inputs[0] || ''
  inputNumberRight.value = inputs[1] || ''
  const query: OperatorObject = {}

  if (inputs[0]) {
    const formattedLeft = parseFloat(inputs[0] || '')
    if (!isNaN(formattedLeft)) {
      query['>='] = {
        value: formattedLeft.toString(),
      }
    } else {
      delete query['>=']
    }
  }

  if (inputs[1]) {
    const formattedRight = parseFloat(inputs[1] || '')
    if (!isNaN(formattedRight)) {
      query['<='] = {
        value: formattedRight.toString(),
      }
    } else {
      delete query['<=']
    }
  }

  debounce(750, () => {
    emits('onSearch', query)
  })
}

function onDateInput(date: { start?: Date; end?: Date }) {
  const { start, end } = date
  emits('onSearch', {
    '>=': {
      value: start?.toISOString(),
    },
    '<=': {
      value: end?.toISOString(),
    },
  })
}
function onEnumInput(value: string) {
  emits('onSearch', {
    '=': {
      value,
    },
  })
}

function getInputType(column: SearchQueryKeys<L>) {
  const schemaObj = store.getSchemaReferencedObject(getSchemaObject(column as string))
  if (!schemaObj) throw new Error(`Column '${column.toString()}' is not present in the schema`)

  if ('metaType' in schemaObj && schemaObj.metaType === 'money') return 'between'
  return schemaObj.type
}

const inputType = ref<JSSchemaObjectTypes['type'] | 'between'>(getInputType(props.activeColumn))

function emitTextSearch(column?: string, smartSearchEnabled?: boolean) {
  const opObject: OperatorObject = {
    '=': {
      value: inputText.value,
    },
  }
  if (smartSearchEnabled === false) {
    opObject['='].smartSearchEnabled = false
  } else if (smartSearchEnabled === true) {
    opObject['='].smartSearchEnabled = true
  } else if (props.filterQuery[props.activeColumn]?.['=']?.smartSearchEnabled) {
    opObject['='].smartSearchEnabled =
      props.filterQuery[props.activeColumn]?.['=']?.smartSearchEnabled
  } else {
    opObject['='].smartSearchEnabled = true
  }
  emits('onSearch', opObject, column as SearchQueryKeys<L>)
}

function toggleSmartSearch(column: string) {
  emitTextSearch(
    column,
    props.filterQuery[column]?.['=']?.smartSearchEnabled != false ? false : true
  )
}

function emitAutoCompleteTrigger() {
  emits('onInputChange', inputText.value)
}
function emitActiveColumn(column: SearchQueryKeys<L>) {
  emits('onActiveColumnChange', column)
}

function onAutocompleteSelect(value: string) {
  inputText.value = value
  autoCompleteOpen.value = false
  debounce(0, emitTextSearch)
}

function onBlur() {
  setTimeout(() => (autoCompleteOpen.value = false), 100)
}

function removeColumn(column: string) {
  if (column === props.activeColumn) {
    inputText.value = ''

    autoCompleteOpen.value = false
    inputNumberLeft.value = ''
    inputNumberRight.value = ''
  }

  debounce(0, () => {
    emits('onRemoveColumn', column as SearchQueryKeys<L>)
  })
}

// function getFormattedValue(column: string, op: '=' | '>' | '<' | '>=' | '<=') {
//   const obj = store.getSchemaReferencedObject(getSchemaObject(column))
//   if (obj.type == 'number') {
//     const formatted =
//       obj.metaType == 'money'
//         ? parseFloat(props.filterQuery[column]?.[op]?.value as string)
//             .toLocaleString('nb-NO', {
//               style: 'currency',
//               currency: 'NOK',
//               minimumFractionDigits: 0,
//               maximumFractionDigits: 0,
//             })
//             .replace('kr', '')
//         : parseFloat(props.filterQuery[column]?.[op]?.value as string).toLocaleString('nb-NO')

//     return formatted
//   }

//   if (obj.type == 'date')
//     return new Date(props.filterQuery[column]?.[op]?.value as string).toLocaleDateString('nb-NO')

//   return props.filterQuery[column]?.[op]?.value
// }

function setNullValue(equals: boolean) {
  debounce(0, () => {
    emits(
      'onSearch',
      { [equals ? '=' : '!=']: { value: null } },
      props.activeColumn as SearchQueryKeys<L>
    )
  })
}

function shouldHideColumn(column: string) {
  return false //!!props.defaultColumns?.includes(column)
}

const activeColumnComputed = computed(() => {
  return store.getSchemaReferencedObject(getSchemaObject(String(props.activeColumn)))
})

const searchedColumns = computed(() => {
  const searchedMap: Record<string, [string, OperatorObject][]> = {}
  Object.entries(props.filterQuery).forEach((entry) => {
    // if (props.defaultColumns?.includes(entry[0]) && entry) return

    const schemaObj = getSchemaObject(entry[0])
    const group: string = schemaObj?.meta?.group ?? 'Other'
    if (!searchedMap[group]) searchedMap[group] = []
    searchedMap[group].push(entry)
  })
  return searchedMap
})
const searchedColumnsArray = computed(() => {
  return Object.entries(searchedColumns.value)
})
const searchedColumnsArrayLength = computed(() => {
  return searchedColumnsArray.value.reduce((acc, curr) => acc + curr[1].length, 0)
})
</script>
<template>
  <!-- <SectionCard>
    <h1 class="text-base font-semibold text-slate-500">Search</h1> -->
  <div
    :class="{
      'flex w-full gap-2': !multipleLines,
      'flex flex-wrap w-full gap-2': multipleLines,
    }"
  >
    <div class="flex gap-2 w-full">
      <Select
        :model-value="String(activeColumn)"
        :defaultValue="String(activeColumn)"
        @update:model-value="onColumnSelect"
      >
        <SelectTrigger class="w-fit px-6 bg-white">
          <SelectValue placeholder="Select the column to search" />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectLabel>Column</SelectLabel>
            <SelectItem v-for="col in searchableColumns" :key="col.key" :value="String(col.key)">
              {{ col.label }}
            </SelectItem>
          </SelectGroup>
          <SelectGroup v-if="!searchableColumns.find((a) => a.key == String(activeColumn))">
            <HorizontalLine />
            <SelectItem class="mt-1" :value="String(activeColumn)">
              {{ activeColumnComputed.humanReadable }}
            </SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select>

      <div class="w-full transition-all relative flex-1">
        <div class="w-full flex gap-2">
          <div class="h-full flex" v-if="activeColumnComputed.optional">
            <Button
              variant="secondary"
              class="h-full rounded-r-none"
              @click="setNullValue(false)"
              :class="{
                ' shadow-md border-primary bg-primary text-white hover:bg-primary/90':
                  filterQuery[String(activeColumn)]?.['!=']?.value === null,
              }"
            >
              Is not Null
            </Button>
            <Button
              variant="secondary"
              class="h-full rounded-l-none"
              @click="setNullValue(true)"
              :class="{
                ' shadow-md border-primary bg-primary text-white hover:bg-primary/90':
                  filterQuery[String(activeColumn)]?.['=']?.value === null,
              }"
            >
              Is Null
            </Button>
          </div>
          <div v-if="inputType === 'boolean'" class="w-full flex gap-2">
            <Button
              :variant="'outline'"
              class="h-full"
              @click="onEnumInput('true')"
              :class="{
                'bg-slate-200': inputText === 'true',
              }"
            >
              True
            </Button>
            <Button
              :variant="'outline'"
              class="h-full"
              @click="onEnumInput('false')"
              :class="{
                'bg-slate-200': inputText === 'false',
              }"
            >
              False
            </Button>
          </div>
          <div
            v-if="inputType === 'enum'"
            class="w-full flex flex-wrap gap-1 bg-slate-50 p-1 rounded-md border-slate-200 border"
          >
            <Badge
              v-for="[column, value] in Object.entries((activeColumnComputed as any).options)"
              :key="column"
              :variant="'outline'"
              @click="onEnumInput(value as string)"
              :value="column"
              :class="{
                'bg-white cursor-pointer': true,
                'shadow-md border-primaryBlue border-2 bg-white cursor-pointer hover:bg-primaryBlue/10':
                  filterQuery[String(activeColumn)]?.['=']?.value === value,
              }"
            >
              {{ value }}
            </Badge>
          </div>
          <div v-else-if="inputType === 'between'" class="w-full flex">
            <NumberBetweenInput
              :model-value="[inputNumberLeft, inputNumberRight]"
              @on-input-change="onNumberInput"
            >
              {{ activeColumnComputed.humanReadable }}
            </NumberBetweenInput>
          </div>
          <div v-else-if="inputType === 'number'" class="w-full flex">
            <Input
              @input="onTextInput"
              :modelValue="inputText"
              type="number"
              :placeholder="'Search...'"
              @keydown.enter="onEnter"
              class="bg-white"
            />
          </div>

          <DateRangePicker
            v-else-if="inputType === 'date'"
            class="w-full"
            @update:change="onDateInput"
          />
          <!-- <div v-else-if="inputType === 'date'" class="w-full">
            <Calendar

          </div> -->

          <div
            v-else-if="inputType === 'string'"
            class="w-full focus-within:bg-slate-200 relative"
            @focusin="autoCompleteOpen = true"
          >
            <Input
              @input="onTextInput"
              :modelValue="inputText"
              :placeholder="'Search...'"
              @focus="autoCompleteOpen = true"
              @blur="onBlur"
              @keydown.enter="onEnter"
              class="bg-white"
            />
            <!-- TODO Add autocomplete back in -->
            <div
              v-if="autoCompleteOpen && inputText?.length > 0 && false"
              :class="{
                'absolute z-50 top-10 left-0 w-full bg-white border border-slate-200 rounded-b-lg': true,
              }"
              tabindex="0"
            >
              <template v-for="item in props.autocomplete" :key="item">
                <div
                  @click="onAutocompleteSelect(item)"
                  class="p-2 px-4 hover:bg-slate-100 cursor-pointer"
                  v-if="item"
                >
                  {{ item }}
                </div>
              </template>

              <div v-if="!props.autocomplete?.length">
                <div class="p-2 px-4">No results found</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="flex shrink-0 gap-2"
      :class="{
        'border-t border-slate-200 pt-2 w-full': multipleLines,
      }"
    >
      <span v-if="multipleLines" class="font-semibold text-sm my-auto">
        {{ searchedColumnsArrayLength ? 'Active search:' : 'No active search' }}
      </span>
      <div
        v-if="searchedColumnsArrayLength < 3 || multipleLines"
        class="h-full flex items-center flex-1 w-full gap-1"
      >
        <template v-for="[group, entries] in searchedColumnsArray" :key="group">
          <!-- <div
              class="text-xs font-semibold p-1 mr-1 text-nowrap"
              :style="{
                color: props.groups?.[group]?.color.accent || 'lightGray',
                }"
                >
              {{ group }}:
            </div> -->
          <template v-for="[column, value] in entries" :key="column">
            <ColumnSearchBadge
              :js-schema="jsSchema"
              :column="column"
              :value="value"
              :active-column="activeColumn"
              :groups="props.groups"
              :group="group"
              :is-active="activeColumn === column"
              :filter-query="filterQuery"
              @column-select="onColumnSelect"
              @toggle-smart-search="toggleSmartSearch"
              @remove-column="removeColumn"
            />
          </template>
        </template>
      </div>
      <DropdownMenu v-else>
        <DropdownMenuTrigger as-child>
          <Button variant="outline" class="flex gap-2"
            ><ListFilter :size="14" /> +{{ searchedColumnsArrayLength }} Filters</Button
          >
        </DropdownMenuTrigger>
        <DropdownMenuContent class="gap-0 flex flex-col">
          <div v-for="[group, entries] in searchedColumnsArray" :key="group">
            <DropdownMenuLabel
              class="font-semibold p-1 text-nowrap"
              :style="{
                color: props.groups?.[group]?.color.accent || 'lightGray',
              }"
            >
              {{ group }}:
            </DropdownMenuLabel>
            <DropdownMenuSeparator
              :style="{
                backgroundColor: props.groups?.[group]?.color.accent || 'lightGray',
              }"
            />
            <div v-for="[column, value] in entries" :key="column">
              <Badge
                v-if="!shouldHideColumn(column)"
                class="p-0 w-full"
                :variant="activeColumn === column ? 'outline' : 'secondary'"
              >
                <div
                  class="h-full items-center p-0.5 cursor-pointer gap-2 hover:bg-gray-50 flex text-nowrap w-full"
                  @click="onColumnSelect(column)"
                >
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <Search
                          :size="12"
                          :style="{
                            color: props.groups?.[group]?.color.accent || 'lightGray',
                          }"
                        />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{{ group }}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  <template v-if="value?.['=']?.value">
                    {{ store.getSchemaReferencedObject(getSchemaObject(column)).humanReadable }}
                    {{ '=' }}
                    <span
                      :class="
                        store.getSchemaReferencedObject(getSchemaObject(column)).sensitive
                          ? 'sentry-mask'
                          : ''
                      "
                      >{{ value['='].value }}</span
                    >
                  </template>
                  <template v-else>
                    <span class="flex gap-2" v-if="value['>=']">
                      <span
                        :class="
                          store.getSchemaReferencedObject(getSchemaObject(column)).sensitive
                            ? 'sentry-mask'
                            : ''
                        "
                      >
                        {{ getFormattedValue(column, '>=') }}</span
                      ><ChevronLeft :size="12" />
                    </span>
                    {{ store.getSchemaReferencedObject(getSchemaObject(column)).humanReadable }}
                    <span class="flex gap-2" v-if="value['<=']">
                      <ChevronLeft :size="12" />
                      <span
                        :class="
                          store.getSchemaReferencedObject(getSchemaObject(column)).sensitive
                            ? 'sentry-mask'
                            : ''
                        "
                      >
                        {{ getFormattedValue(column, '<=') }}
                      </span>
                    </span>
                  </template>
                </div>
                <template
                  v-if="
                    value?.['=']?.value &&
                    store.getSchemaReferencedObject(getSchemaObject(column)).type === 'string'
                  "
                >
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <div
                          v-if="value['='].smartSearchEnabled != false"
                          class="hover:bg-red-200 rounded-sm flex items-center h-full p-1"
                          @click="toggleSmartSearch(column)"
                        >
                          <Sparkles :size="12" />
                        </div>
                        <div
                          v-else
                          class="hover:bg-red-200 rounded-sm flex items-center h-full p-1"
                          @click="toggleSmartSearch(column)"
                        >
                          <Sparkle :size="12" /></div
                      ></TooltipTrigger>
                      <TooltipContent>
                        <p>Smart search ✨</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </template>
                <div
                  class="hover:bg-red-200 cursor-pointer rounded-sm flex items-center h-full p-1"
                  @click="removeColumn(column)"
                >
                  <X :size="12" />
                </div>
              </Badge>
            </div>
            <DropdownMenuSeparator />
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      <!-- <Separator v-if="!$slots.default" orientation="vertical" /> -->
      <slot />
    </div>
  </div>
  <!-- </SectionCard> -->
</template>
